import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  useGetMachinesQuery,
  useProvisionLinkMutation,
  useSetWifiMutation,
} from '../../features/thirdPartyIntegrations';
import emLegacyTheme from '../../theme/emLegacyTheme';

export interface ThirdPartyIntegrationsProps {
  apiHost?: string;
  currentCustomerGroupUuid?: string;
}

const ThirdPartyIntegrations: React.FC<ThirdPartyIntegrationsProps> = ({
  apiHost,
  currentCustomerGroupUuid,
}) => {
  const [filterValue, setFilterValue] = useState('');
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');
  const [wifiSSID, setWifiSSID] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [linkName, setLinkName] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [selectedMachines, setSelectedMachines] = useState<Set<number>>(
    new Set(),
  );
  const [successMessage, setSuccessMessage] = useState('');

  const {
    data: machineData = [],
    error,
    isLoading: loading,
  } = useGetMachinesQuery(
    debouncedFilterValue ? { serial_filter: debouncedFilterValue } : {},
    {
      pollingInterval: 0,
      refetchOnMountOrArgChange: true,
    },
  );

  const [
    provisionLink,
    {
      error: provisionError,
      isLoading: provisionLoading,
      reset: resetProvision,
    },
  ] = useProvisionLinkMutation();
  const [
    setWifi,
    { error: wifiError, isLoading: wifiLoading, reset: resetWifi },
  ] = useSetWifiMutation();

  const getErrorMessage = (error: any): string => {
    if (!error) return '';

    if ('status' in error) {
      if (error.data && typeof error.data === 'object') {
        return error.data.message || error.data.error || 'An error occurred';
      }
      return `Error: ${error.status}`;
    }

    if ('message' in error) {
      return error.message || 'An error occurred';
    }

    return 'An unexpected error occurred';
  };

  const clearMessages = () => {
    setSuccessMessage('');
    resetProvision();
    resetWifi();
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilterValue(filterValue);
    }, 1000); // 1000ms debounce

    return () => clearTimeout(timeoutId);
  }, [filterValue]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    clearMessages();
    if (event.target.checked) {
      const allMachineIds = new Set(machineData.map((machine) => machine.id));
      setSelectedMachines(allMachineIds);
    } else {
      setSelectedMachines(new Set());
    }
  };

  const handleSelectMachine = (machineId: number) => {
    clearMessages();
    const newSelected = new Set(selectedMachines);
    if (newSelected.has(machineId)) {
      newSelected.delete(machineId);
    } else {
      newSelected.add(machineId);
    }
    setSelectedMachines(newSelected);
  };

  const isSelected = (machineId: number) => selectedMachines.has(machineId);
  const isAllSelected =
    machineData.length > 0 && selectedMachines.size === machineData.length;
  const isIndeterminate =
    selectedMachines.size > 0 && selectedMachines.size < machineData.length;

  const getSelectedMachineDetails = () => {
    return machineData.filter((machine) => selectedMachines.has(machine.id));
  };

  const isProvisionDisabled =
    selectedMachines.size === 0 || !linkName.trim() || !serialNumber.trim();
  const isSetWifiDisabled =
    selectedMachines.size === 0 ||
    !linkName.trim() ||
    !serialNumber.trim() ||
    !wifiSSID.trim() ||
    !wifiPassword.trim();

  const handleProvision = async () => {
    clearMessages();
    if (!apiHost) {
      return;
    }

    const selectedMachineDetails = getSelectedMachineDetails();

    try {
      await provisionLink({
        apiHost,
        data: {
          customer_group_uuid: currentCustomerGroupUuid || '',
          link_name: linkName.trim(),
          managed_machines: selectedMachineDetails.map(
            (machine) => machine.uuid,
          ),
          serial_number: serialNumber.trim(),
        },
      }).unwrap();

      setSuccessMessage('Link provisioned successfully');
    } catch (err) {
      console.error('Provisioning failed:', err);
    }
  };

  const handleSetWifi = async () => {
    clearMessages();
    if (!apiHost) {
      return;
    }

    try {
      await setWifi({
        apiHost,
        data: {
          customer_group_uuid: currentCustomerGroupUuid || '',
          link_name: linkName.trim(),
          password: wifiPassword.trim(),
          serial_number: serialNumber.trim(),
          ssid: wifiSSID.trim(),
        },
      }).unwrap();

      setSuccessMessage('WiFi configuration set successfully');
    } catch (err) {
      console.error('WiFi configuration failed:', err);
    }
  };

  return (
    <ThemeProvider theme={emLegacyTheme}>
      <CssBaseline />
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Typography component="h1" gutterBottom variant="h4">
            Third-Party Integrations
          </Typography>
          <Box sx={{ mb: 3 }}>
            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              disabled={loading}
              fullWidth
              onChange={(e) => {
                setFilterValue(e.target.value);
                clearMessages();
              }}
              placeholder="Filter by serial number, name, or MAC address..."
              sx={{ maxWidth: 500 }}
              value={filterValue}
              variant="outlined"
            />
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {getErrorMessage(error)}
            </Alert>
          )}
          {provisionError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {getErrorMessage(provisionError)}
            </Alert>
          )}
          {wifiError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {getErrorMessage(wifiError)}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}
          {selectedMachines.size > 0 && (
            <Box sx={{ mb: 2 }}>
              <Chip
                color="primary"
                label={`${selectedMachines.size} machine${selectedMachines.size === 1 ? '' : 's'} selected`}
                size="medium"
                variant="outlined"
              />
            </Box>
          )}
          <Paper sx={{ mb: 4 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isAllSelected}
                        disabled={loading || machineData.length === 0}
                        indeterminate={isIndeterminate}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Serial Number</strong>
                    </TableCell>
                    <TableCell>
                      <strong>MAC Address</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Machine Model</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Partner</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Customer</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ py: 4, textAlign: 'center' }}
                      >
                        <CircularProgress size={24} />
                        <Typography sx={{ mt: 1 }} variant="body2">
                          Loading machines...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    machineData.map((machine) => {
                      const isItemSelected = isSelected(machine.id);
                      return (
                        <TableRow
                          hover
                          key={machine.id}
                          onClick={() => handleSelectMachine(machine.id)}
                          selected={isItemSelected}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              onChange={() => handleSelectMachine(machine.id)}
                            />
                          </TableCell>
                          <TableCell>{machine.name}</TableCell>
                          <TableCell>{machine.serial_number}</TableCell>
                          <TableCell>{machine.mac_address}</TableCell>
                          <TableCell>{machine.type}</TableCell>
                          <TableCell>{machine.machine_model}</TableCell>
                          <TableCell>{machine.partner}</TableCell>
                          <TableCell>{machine.customer}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && machineData.length === 0 && !error && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">
                  {filterValue
                    ? 'No machines found matching your filter criteria.'
                    : 'No machines available.'}
                </Typography>
              </Box>
            )}
          </Paper>
          <Paper sx={{ p: 3 }}>
            <Typography component="h2" gutterBottom variant="h5">
              Anylink
            </Typography>
            <Box sx={{ mb: 4 }}>
              <Typography
                component="h3"
                gutterBottom
                sx={{ mb: 2 }}
                variant="h6"
              >
                Link Configuration
              </Typography>
              <Grid container spacing={3}>
                <Grid item sm={6} xs={12}>
                  <TextField
                    fullWidth
                    inputProps={{ 'data-testid': 'anylink-link-name-input' }}
                    label="Link Name"
                    onChange={(e) => {
                      setLinkName(e.target.value);
                      clearMessages();
                    }}
                    placeholder="Enter link name"
                    required
                    value={linkName}
                    variant="outlined"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <TextField
                    fullWidth
                    inputProps={{
                      'data-testid': 'anylink-serial-number-input',
                    }}
                    label="Serial Number"
                    onChange={(e) => {
                      setSerialNumber(e.target.value);
                      clearMessages();
                    }}
                    placeholder="Enter serial number"
                    required
                    value={serialNumber}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ mb: 4 }}>
              <Button
                color="primary"
                data-testid="anylink-provision-button"
                disabled={isProvisionDisabled || provisionLoading}
                onClick={handleProvision}
                size="large"
                startIcon={
                  provisionLoading ? <CircularProgress size={20} /> : undefined
                }
                sx={{ mr: 2 }}
                variant="contained"
              >
                {provisionLoading ? 'Provisioning...' : 'Provision'}
              </Button>
              {(isProvisionDisabled || provisionLoading) &&
                !provisionLoading && (
                  <Typography
                    color="text.secondary"
                    sx={{ ml: 1 }}
                    variant="caption"
                  >
                    {selectedMachines.size === 0
                      ? 'Select machines and fill required fields to provision'
                      : 'Fill in Link Name and Serial Number to provision'}
                  </Typography>
                )}
            </Box>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSetWifi();
              }}
            >
              <Typography
                component="h3"
                gutterBottom
                sx={{ mb: 2 }}
                variant="h6"
              >
                WiFi Configuration
              </Typography>
              <Grid alignItems="end" container spacing={3}>
                <Grid item sm={4} xs={12}>
                  <TextField
                    fullWidth
                    inputProps={{ 'data-testid': 'anylink-wifi-ssid-input' }}
                    label="WiFi SSID"
                    onChange={(e) => {
                      setWifiSSID(e.target.value);
                      clearMessages();
                    }}
                    placeholder="Enter WiFi network name"
                    required
                    value={wifiSSID}
                    variant="outlined"
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <TextField
                    fullWidth
                    inputProps={{
                      'data-testid': 'anylink-wifi-password-input',
                    }}
                    label="WiFi Password"
                    onChange={(e) => {
                      setWifiPassword(e.target.value);
                      clearMessages();
                    }}
                    placeholder="Enter WiFi password"
                    required
                    type="password"
                    value={wifiPassword}
                    variant="outlined"
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <Button
                    color="primary"
                    disabled={isSetWifiDisabled || wifiLoading}
                    fullWidth
                    size="large"
                    startIcon={
                      wifiLoading ? <CircularProgress size={20} /> : undefined
                    }
                    type="submit"
                    variant="contained"
                  >
                    {wifiLoading ? 'Setting WiFi...' : 'Set WiFi'}
                  </Button>
                </Grid>
              </Grid>
              {(isSetWifiDisabled || wifiLoading) && !wifiLoading && (
                <Typography
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                  variant="caption"
                >
                  {selectedMachines.size === 0
                    ? 'Select machines and fill all required fields to set WiFi'
                    : 'Fill in all required fields to set WiFi'}
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default ThirdPartyIntegrations;
